import { useEffect, useState } from "react";
import { ledgerApi, unwrap, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Link } from "react-router-dom";
import { Button, Card, Empty, Field, Input, Loader, Pagination, StatusPill } from "../components/ui";
import { entryLabel, formatDate, formatMoney, formatMonth } from "../utils/format";
import { isoMonth, paymentLabel } from "../utils/finance";

const emptyDayFilters = { date: "", from: "", to: "" };

export default function Ledger() {
  const { query, ready } = useBuilderQuery();
  const toast = useToast();
  const [view, setView] = useState("days");
  const [filters, setFilters] = useState(emptyDayFilters);
  const [applied, setApplied] = useState(emptyDayFilters);
  const [month, setMonth] = useState(isoMonth());
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDate = async (date) => {
    setDetailLoading(true);
    try {
      const res = await ledgerApi.byDate(date, query);
      setSelected(unwrap(res));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadDays = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const listQuery = { ...query, page, limit: 31 };
      if (applied.date) listQuery.date = applied.date;
      else {
        if (applied.from) listQuery.from = applied.from;
        if (applied.to) listQuery.to = applied.to;
      }
      const res = await ledgerApi.list(listQuery);
      const data = unwrapList(res);
      const list = data.items;
      setItems(list);
      setPagination(data.pagination);
      if (applied.date) openDate(applied.date);
      else if (list[0]?.date) openDate(list[0].date);
      else setSelected(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMonth = async () => {
    if (!ready || !month) {
      setMonthSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMonthSummary(unwrap(await ledgerApi.month(month, query)));
    } catch (error) {
      toast.error(error.message);
      setMonthSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "month") loadMonth();
    else loadDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, page, applied.date, applied.from, applied.to, month, query.builderId]);

  const switchView = (next) => {
    setView(next);
    setSelected(null);
    setLoading(true);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  };

  const clearFilters = () => {
    setFilters(emptyDayFilters);
    setApplied(emptyDayFilters);
    setPage(1);
  };

  const renderEntries = (entries = []) =>
    entries.length === 0 ? (
      <Empty title="No entries" />
    ) : (
      <div className="list">
        {entries.map((entry) => (
          <div key={entry.id} className="list-row">
            <div>
              <b>{entry.description}</b>
              <span>
                {entry.date ? `${formatDate(entry.date)} · ` : ""}
                {entryLabel(entry.type)}
                {entry.vendorName ? ` · ${entry.vendorName}` : ""}
                {entry.paymentType ? ` · ${paymentLabel(entry.paymentType)}` : ""}
                {entry.bankName || entry.bank?.name ? ` · ${entry.bankName || entry.bank.name}` : ""}
                {entry.affectsBalance === false ? " · Khata only" : ""}
              </span>
            </div>
            <strong className={entry.direction === "credit" ? "plus" : "minus"}>
              {entry.direction === "credit" ? "+" : "−"} {formatMoney(entry.amount)}
            </strong>
          </div>
        ))}
      </div>
    );

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">One page for each day</p>
          <h1>Daily book</h1>
        </div>
        <div className="head-actions">
          <div className="view-toggle">
            <Button variant={view === "days" ? "primary" : "ghost"} onClick={() => switchView("days")}>
              By day
            </Button>
            <Button variant={view === "month" ? "primary" : "ghost"} onClick={() => switchView("month")}>
              By month
            </Button>
          </div>
          <Link className="btn btn-ghost btn-sm" to="/ledger/statement">
            Full statement
          </Link>
          {view === "month" && month ? (
            <Link className="btn btn-ghost btn-sm" to={`/ledger/statement?month=${month}`}>
              Month statement
            </Link>
          ) : null}
        </div>
      </header>

      {view === "days" ? (
        <form className="filters" onSubmit={applyFilters}>
          <Field label="One date">
            <Input
              type="date"
              value={filters.date}
              onChange={(event) => setFilters((prev) => ({ ...prev, date: event.target.value, from: "", to: "" }))}
            />
          </Field>
          <Field label="From">
            <Input
              type="date"
              value={filters.from}
              onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value, date: "" }))}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={filters.to}
              onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value, date: "" }))}
            />
          </Field>
          <Button type="submit" variant="ghost">
            Show
          </Button>
          <Button type="button" variant="ghost" onClick={clearFilters}>
            All days
          </Button>
        </form>
      ) : (
        <form
          className="filters"
          onSubmit={(event) => {
            event.preventDefault();
            loadMonth();
          }}
        >
          <Field label="Month">
            <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
          </Field>
          <Button type="submit" variant="ghost">
            Show month
          </Button>
        </form>
      )}

      {loading ? (
        <Loader />
      ) : view === "month" ? (
        !monthSummary || !(monthSummary.days || []).length ? (
          <Empty title="No book for this month" text="Pick another month." />
        ) : (
          <div className="stack">
            <div className="stat-grid compact">
              <article className="stat">
                <span>{formatMonth(monthSummary.month)} start</span>
                <strong>{formatMoney(monthSummary.openingBalance)}</strong>
              </article>
              <article className="stat stat-in">
                <span>Money in</span>
                <strong>{formatMoney(monthSummary.totalCredits)}</strong>
              </article>
              <article className="stat stat-out">
                <span>Spent</span>
                <strong>{formatMoney(monthSummary.totalDebits)}</strong>
              </article>
              <article className="stat stat-now">
                <span>End</span>
                <strong>{formatMoney(monthSummary.closingBalance)}</strong>
              </article>
            </div>
            <div className="ledger-layout">
              <div className="ledger-days">
                {monthSummary.days.map((item) => (
                  <button
                    key={item.id || item.date}
                    className={`day-chip ${selected?.date === item.date ? "active" : ""}`}
                    onClick={() => openDate(item.date)}
                  >
                    <span>{formatDate(item.date)}</span>
                    <b>{formatMoney(item.closingBalance)}</b>
                    <StatusPill status={item.status} />
                  </button>
                ))}
              </div>
              <Card className="ledger-detail">
                {detailLoading ? <Loader /> : selected ? (
                  <>
                    <div className="card-head">
                      <h2>{formatDate(selected.date)}</h2>
                      <div className="head-actions">
                        <StatusPill status={selected.status} />
                        <Link className="btn btn-ghost btn-sm" to={`/ledger/${selected.date}/statement`}>
                          Statement
                        </Link>
                      </div>
                    </div>
                    {renderEntries(selected.entries)}
                  </>
                ) : (
                  <>
                    <h2>All entries this month</h2>
                    {renderEntries(monthSummary.entries)}
                  </>
                )}
              </Card>
            </div>
          </div>
        )
      ) : items.length === 0 ? (
        <Empty title="Cashbook is empty" text="Set opening cash first, or pick another date." />
      ) : (
        <div className="ledger-layout">
          <div className="ledger-days">
            {items.map((item) => (
              <button
                key={item.id || item.date}
                className={`day-chip ${selected?.date === item.date ? "active" : ""}`}
                onClick={() => openDate(item.date)}
              >
                <span>{formatDate(item.date)}</span>
                <b>{formatMoney(item.closingBalance)}</b>
                <StatusPill status={item.status} />
              </button>
            ))}
            <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />
          </div>

          <Card className="ledger-detail">
            {detailLoading || !selected ? (
              <Loader />
            ) : (
              <>
                <div className="card-head">
                  <h2>{formatDate(selected.date)}</h2>
                  <div className="head-actions">
                    <StatusPill status={selected.status} />
                    <Link className="btn btn-ghost btn-sm" to={`/ledger/${selected.date}/statement`}>
                      Statement
                    </Link>
                  </div>
                </div>
                <div className="stat-grid compact">
                  <article className="stat">
                    <span>Start</span>
                    <strong>{formatMoney(selected.openingBalance)}</strong>
                  </article>
                  <article className="stat stat-in">
                    <span>Money in</span>
                    <strong>{formatMoney(selected.totalCredits)}</strong>
                  </article>
                  <article className="stat stat-out">
                    <span>Spent</span>
                    <strong>{formatMoney(selected.totalDebits)}</strong>
                  </article>
                  <article className="stat stat-now">
                    <span>End</span>
                    <strong>{formatMoney(selected.closingBalance)}</strong>
                  </article>
                </div>
                {renderEntries(selected.entries)}
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
